import React, { useState } from 'react';
import { sampleData } from '../../../../../DummyData/ConvertSampleJson';
// Define types for our component
interface JsonToYamlProps {}

// YAML format options
type YamlFormat = 'default' | 'json' | 'yaml1.1' | 'yaml1.2';

// Indentation options
type IndentSize = 2 | 4 | 8;

const JsonToYaml: React.FC<JsonToYamlProps> = () => {
  // State for the JSON input
  const [jsonInput, setJsonInput] = useState<string>('');
  // State for the YAML output
  const [yamlOutput, setYamlOutput] = useState<string>('');
  // State for error messages
  const [error, setError] = useState<string>('');
  // State for the file name
  const [fileName, setFileName] = useState<string>('data');
  // State for loading indicator
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // State for showing advanced options
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  // State for YAML format
  const [yamlFormat, setYamlFormat] = useState<YamlFormat>('default');
  // State for indentation size
  const [indentSize, setIndentSize] = useState<IndentSize>(2);
  // State to track if the conversion happened
  const [isYamlGenerated, setIsYamlGenerated] = useState<boolean>(false);
  // State for copied indicator
  const [copied, setCopied] = useState<boolean>(false);
  // State for quote strings option
  const [quoteStrings, setQuoteStrings] = useState<boolean>(false);
  // State for flowLevel option (-1 for auto)
  const [flowLevel, setFlowLevel] = useState<number>(-1);


  // Handle JSON input change
  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setJsonInput(e.target.value);
    setError('');
    setIsYamlGenerated(false);
  };

  // Handle file name change
  const handleFileNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFileName(e.target.value);
  };

  // Use example data
  const useExampleData = () => {
    setJsonInput(JSON.stringify(sampleData, null, 2));
    setError('');
    setIsYamlGenerated(false);
    setYamlOutput('');
  };

  // Beautify/Format JSON
  const beautifyJson = () => {
    try {
      if (!jsonInput.trim()) {
        setError('Please enter JSON data to format');
        return;
      }
      
      const parsedJson = JSON.parse(jsonInput);
      const beautified = JSON.stringify(parsedJson, null, 2);
      setJsonInput(beautified);
      setError('');
    } catch (e) {
      setError(`Invalid JSON format: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // Validate JSON
  const validateJson = (jsonString: string): boolean => {
    try {
      JSON.parse(jsonString);
      return true;
    } catch (e) {
      return false;
    }
  };

  // Clear all inputs and reset states
  const clearAll = () => {
    setJsonInput('');
    setYamlOutput('');
    setError('');
    setFileName('data');
    setYamlFormat('default');
    setIndentSize(2);
    setIsYamlGenerated(false);
    setQuoteStrings(false);
    setFlowLevel(-1);
  };

  // Helper function to get indent string
  const getIndent = (level: number): string => {
    return ' '.repeat(indentSize * level);
  };

  // Helper function to check if a string needs quotes in YAML
  const needsQuotes = (str: string): boolean => {
    // If user chooses to quote all strings
    if (quoteStrings) return true;
    
    // Check if the string could be interpreted as a number, boolean, null, etc.
    const specialValues = [
      'true', 'false', 'yes', 'no', 'on', 'off',
      'null', 'undefined', '~', 'y', 'n', 'Y', 'N'
    ];
    
    if (specialValues.includes(str)) return true;
    if (!isNaN(Number(str))) return true;
    
    // Check for special characters or patterns
    const needsQuotePattern = /[:{}\[\],&*#?|<>=!%@`\\]/;
    if (needsQuotePattern.test(str)) return true;
    
    // Check if string starts or ends with whitespace
    if (str.trim() !== str) return true;
    
    return false;
  };

  // Helper function to convert a value to YAML string
  const valueToYaml = (value: any, level: number, isArrayItem: boolean = false): string => {
    if (value === null || value === undefined) {
      return 'null';
    }

    if (typeof value === 'string') {
      if (needsQuotes(value)) {
        // Escape quotes and special characters
        const escaped = value
          .replace(/\\/g, '\\\\')
          .replace(/"/g, '\\"')
          .replace(/\n/g, '\\n');
        return `"${escaped}"`;
      }
      return value;
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '[]';
      }
      
      // Flow style (inline) for arrays if flowLevel is set and we're at or beyond that level
      if (flowLevel >= 0 && level >= flowLevel) {
        const items = value.map(item => valueToYaml(item, level + 1, true)).join(', ');
        return `[${items}]`;
      }
      
      // Block style for arrays
      const indent = getIndent(level);
      const nextIndent = getIndent(level + 1);
      
      return '\n' + value.map(item => {
        const itemStr = valueToYaml(item, level + 1, true);
        // If the item is multiline (object/array), handle indentation differently
        if (itemStr.includes('\n')) {
          return `${indent}- ${itemStr.substring(0, itemStr.indexOf('\n'))}\n${itemStr.substring(itemStr.indexOf('\n') + 1).split('\n').map(line => `${nextIndent}${line}`).join('\n')}`;
        }
        return `${indent}- ${itemStr}`;
      }).join('\n');
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      
      if (keys.length === 0) {
        return '{}';
      }
      
      // Flow style (inline) for objects if flowLevel is set and we're at or beyond that level
      if (flowLevel >= 0 && level >= flowLevel) {
        const props = keys.map(key => {
          const k = needsQuotes(key) ? `"${key}"` : key;
          return `${k}: ${valueToYaml(value[key], level + 1)}`;
        }).join(', ');
        return `{${props}}`;
      }
      
      // Block style for objects
      const indent = getIndent(level);
      
      return '\n' + keys.map(key => {
        const keyStr = needsQuotes(key) ? `"${key}"` : key;
        const valueStr = valueToYaml(value[key], level + 1);
        
        // If the value is multiline (object/array), handle indentation
        if (valueStr.startsWith('\n')) {
          return `${indent}${keyStr}:${valueStr}`;
        }
        
        return `${indent}${keyStr}: ${valueStr}`;
      }).join('\n');
    }

    return String(value);
  };

  // Convert JSON to YAML
  const convertJsonToYaml = () => {
    setError('');
    setIsLoading(true);
    setIsYamlGenerated(false);
    setYamlOutput('');
    
    if (!jsonInput.trim()) {
      setError('Please enter JSON data');
      setIsLoading(false);
      return;
    }

    if (!validateJson(jsonInput)) {
      setError('Invalid JSON format. Please check your input.');
      setIsLoading(false);
      return;
    }

    try {
      const jsonData = JSON.parse(jsonInput);
      
      // YAML header if needed
      let yamlHeader = '';
      if (yamlFormat === 'yaml1.1') {
        yamlHeader = '%YAML 1.1\n---\n';
      } else if (yamlFormat === 'yaml1.2') {
        yamlHeader = '%YAML 1.2\n---\n';
      } else if (yamlFormat === 'json') {
        yamlHeader = '%YAML 1.2\n%TAG ! tag:yaml.org,2002:\n---\n';
      }
      
      // Generate YAML content
      const yamlContent = valueToYaml(jsonData, 0).trim();
      
      // Combine header and content
      const yaml = yamlHeader + yamlContent;
      
      setYamlOutput(yaml);
      setIsYamlGenerated(true);
      setIsLoading(false);
    } catch (e) {
      setError(`Error generating YAML: ${e instanceof Error ? e.message : String(e)}`);
      setIsLoading(false);
    }
  };

  // Copy YAML to clipboard
  const copyToClipboard = () => {
    navigator.clipboard.writeText(yamlOutput)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        setError('Failed to copy to clipboard');
      });
  };

  // Download YAML file
  const downloadYaml = () => {
    const blob = new Blob([yamlOutput], { type: 'text/yaml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'data'}.yaml`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">JSON to YAML Converter</h1>
      
      {/* Description */}
      <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
        <p className="text-blue-800">
          Convert JSON data to YAML format. Paste your JSON data below to generate a properly formatted YAML document.
        </p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Input Section */}
        <div className="flex flex-col h-full">
          <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
            <div className="flex justify-between items-center mb-3">
              <label htmlFor="jsonInput" className="font-medium text-gray-700">
                JSON Input
              </label>
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={useExampleData}
                >
                  Use Example
                </button>
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={beautifyJson}
                >
                  Format JSON
                </button>
                <button
                  className="text-sm text-gray-600 hover:text-gray-800"
                  onClick={() => setJsonInput('')}
                >
                  Clear
                </button>
              </div>
            </div>
            <textarea
              id="jsonInput"
              className="w-full h-80 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
              value={jsonInput}
              onChange={handleInputChange}
              placeholder='Paste your JSON data here, e.g., {"name": "John", "age": 30}'
            />
          </div>
          
          {/* Configuration */}
          <div className="bg-white p-4 rounded-md shadow-sm">
            <div className="mb-4">
              <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                File Name
              </label>
              <input
                type="text"
                id="fileName"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                value={fileName}
                onChange={handleFileNameChange}
                placeholder="Enter file name (without extension)"
              />
            </div>
            
            <div className="mb-4">
              <button
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            
            {showAdvanced && (
              <div className="space-y-4 p-3 bg-gray-50 rounded-md mb-4">
                <div>
                  <label htmlFor="yamlFormat" className="block text-sm font-medium text-gray-700 mb-1">
                    YAML Format
                  </label>
                  <select
                    id="yamlFormat"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={yamlFormat}
                    onChange={(e) => setYamlFormat(e.target.value as YamlFormat)}
                  >
                    <option value="default">Default (no header)</option>
                    <option value="yaml1.1">YAML 1.1</option>
                    <option value="yaml1.2">YAML 1.2</option>
                    <option value="json">JSON Schema mode</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="indentSize" className="block text-sm font-medium text-gray-700 mb-1">
                    Indentation Size
                  </label>
                  <select
                    id="indentSize"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={indentSize}
                    onChange={(e) => setIndentSize(Number(e.target.value) as IndentSize)}
                  >
                    <option value="2">2 spaces</option>
                    <option value="4">4 spaces</option>
                    <option value="8">8 spaces</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="flowLevel" className="block text-sm font-medium text-gray-700 mb-1">
                    Flow Level
                  </label>
                  <select
                    id="flowLevel"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    value={flowLevel}
                    onChange={(e) => setFlowLevel(Number(e.target.value))}
                  >
                    <option value="-1">Auto (block style)</option>
                    <option value="0">Inline everything (flow style)</option>
                    <option value="1">Block level 0, flow level 1+</option>
                    <option value="2">Block levels 0-1, flow level 2+</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Flow level determines which nesting levels use compact inline format
                  </p>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="quoteStrings"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    checked={quoteStrings}
                    onChange={(e) => setQuoteStrings(e.target.checked)}
                  />
                  <label htmlFor="quoteStrings" className="ml-2 block text-sm text-gray-700">
                    Quote All Strings
                  </label>
                  <p className="text-xs text-gray-500 ml-6">
                    Quote all string values to avoid YAML interpretation issues
                  </p>
                </div>
              </div>
            )}
            
            <div className="mt-4 flex justify-between">
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                onClick={convertJsonToYaml}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  'Convert to YAML'
                )}
              </button>
              
              <button
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                onClick={clearAll}
              >
                Clear All
              </button>
            </div>
          </div>
        </div>
        
        {/* Output Section */}
        <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <label className="font-medium text-gray-700">
              YAML Output
            </label>
            
            {isYamlGenerated && (
              <div className="flex space-x-2">
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={copyToClipboard}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? 'Copied!' : 'Copy YAML'}
                </button>
                
                <button
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  onClick={downloadYaml}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download
                </button>
              </div>
            )}
          </div>
          
          <div className="flex-grow flex flex-col">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}
            
            {!error && !isYamlGenerated && (
              <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <p>
                    YAML output will appear here after conversion.
                  </p>
                </div>
              </div>
            )}
            
            {!error && isYamlGenerated && (
              <textarea
                className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                value={yamlOutput}
                readOnly
              />
            )}
          </div>
          
          {isYamlGenerated && (
            <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <p className="font-medium">YAML Successfully Generated!</p>
              </div>
              <p className="text-sm">
                Your JSON data has been converted to YAML format with {indentSize}-space indentation.
                {yamlFormat !== 'default' && ` Using ${yamlFormat} format.`}
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Information Section */}
      <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
        <h2 className="text-lg font-semibold mb-4">About JSON to YAML Conversion</h2>
        
        <div className="space-y-4">
          <div>
            <h3 className="font-medium text-gray-800 mb-2">How This Converter Works</h3>
            <p className="text-gray-600">
              This tool converts your JSON data into YAML (YAML Ain't Markup Language) format. YAML is a human-friendly
              data serialization standard that's commonly used for configuration files and in applications where data is being stored
              or transmitted. YAML is designed to be more readable and writable by humans than JSON.
            </p>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Features</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li><strong>Format Options</strong> - Choose between different YAML versions or no header</li>
              <li><strong>Indentation Control</strong> - Select 2, 4, or 8 spaces for indentation</li>
              <li><strong>Flow Level Control</strong> - Choose between block style (expanded) or flow style (compact)</li>
              <li><strong>String Quoting</strong> - Option to quote all strings to avoid YAML interpretation issues</li>
              <li><strong>Copy & Download</strong> - Easily copy the YAML to clipboard or download as a file</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">Tips for Better Results</h3>
            <ul className="list-disc pl-5 text-gray-600">
              <li>Use the "Format JSON" button to properly format and validate your JSON data before conversion</li>
              <li>For configuration files, use block style (the default) for better readability</li>
              <li>For data transmission or when space efficiency matters, use flow style</li>
              <li>Enable "Quote All Strings" if you're concerned about strings being interpreted as numbers or booleans</li>
              <li>If your data contains special characters, always check the output for correct formatting</li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-medium text-gray-800 mb-2">YAML vs JSON</h3>
            <p className="text-gray-600">
              While JSON and YAML are both data serialization formats, YAML offers several advantages:
            </p>
            <ul className="list-disc pl-5 text-gray-600">
              <li>YAML allows comments, making it better for configuration files</li>
              <li>YAML uses indentation for structure, making it more readable</li>
              <li>YAML supports complex data types like anchors and references</li>
              <li>YAML doesn't require quotes around strings in most cases</li>
              <li>YAML is more human-friendly while JSON is more machine-friendly</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JsonToYaml;